import axios, { AxiosError, AxiosResponse } from 'axios';
import { FormikHelpers } from 'formik';
import React, { useState, useEffect, ReactNode } from 'react';
import { Alert } from 'react-bootstrap';
import ReactDOM from 'react-dom';
import { Seller, SellerContact } from '../../../../structures/models/seller';
import { ApiV1Error, isCSRFError, isValidationError } from '../../../../structures/responses/axios';
import { LaravelValidationErrors } from '../../../../structures/responses/errors';
import routes from '../../../../utils/routes';
import EditSellerContact from '../../../forms/seller-contacts/edit-seller-contact';
import useModal from '../../../../utils/hooks/useModal';
import ConfirmationModal from '../../../search/widgets/ConfirmationModal';
import modifyDeleteErrorMessage from '../../../../utils/sellerContacts';
import ContactsTable from './partials/contacts-table';
import ContactLine from './partials/contact-line';

type ManageContactsProps = {
  contacts: SellerContact[],
  seller: Seller,
}

type SellerUpdatePrimaryRequest = Seller & {
  primary_contact_id: number,
}

const ManageContacts: React.FC<ManageContactsProps> = ({
  contacts,
  seller,
}) => {
  const [isHidden, setIsHidden] = useState(false);
  const [currentlyEditing, setCurrentlyEditing] = useState<SellerContact | undefined>(undefined);
  const [savedMessage, setSavedMessage] = useState('');
  const [sellerState, setSellerState] = useState(seller);
  const [primaryContact, setPrimaryContact] = useState(sellerState.primaryContact);
  const [contactsState, setContactsState] = useState(contacts);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>('');
  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
  } = useModal();

  // If the user begins to edit a different contact or load a request,
  // clear any active messages
  useEffect(() => {
    if (currentlyEditing || isLoading) {
      setSavedMessage('');
      setErrorMessage('');
    }
  }, [currentlyEditing, isLoading]);

  // If the seller changes, set the primary contact based on the new seller
  useEffect(() => {
    setPrimaryContact(sellerState.primaryContact);
  }, [sellerState]);

  // If the user clicks the cancel button, close the edit form
  // and disable any loading states
  const onCancel = () => {
    setIsLoading(false);
    setCurrentlyEditing(undefined);
  };

  // Delete button handler. Opens delete confirmation modal. Deletes a seller contact.
  const onDelete = (contact: SellerContact) => {
    setIsLoading(true);

    setModalData({
      message: `Would you like to delete this seller contact: ${contact.name}?`,
      onConfirmAsync: () => {
        return new Promise((resolve, reject) => {
          return axios.delete(routes.apiSellerContactsDelete(contact.id))
            .then((res: AxiosResponse<string>) => {
              if (res.status === 200) {
                setContactsState(contactsState.filter((loopContact) => {
                  return loopContact.id !== contact.id;
                }));
                setIsLoading(false);
                setSavedMessage(`${contact.name} deleted.`);

                resolve(`${contact.name} deleted.`);
              }
            })
            .catch((e: AxiosError) => {
              setIsLoading(false);

              const statusCode = e.response?.status;

              if (statusCode && statusCode === 419) {
                setErrorMessage('This page must be refreshed.');
              } else if (
                statusCode
                && statusCode === 409
                && e.response?.data
                && typeof e.response.data === 'string'
              ) {
                setErrorMessage(e.response.data);
              } else {
                const defaultCatchErrorMessage: string = `An error occurred while deleting ${contact.name}`;

                // Get errors from response.
                const catchErrors: object|undefined = e.response?.data?.errors;

                // Modify error message.
                const newCatchErrorMessage: string = modifyDeleteErrorMessage(catchErrors);

                setErrorMessage(defaultCatchErrorMessage);

                reject(newCatchErrorMessage || defaultCatchErrorMessage);
              }
            });
        });
      },
      onClose: () => {
        setIsLoading(false);

        setModalData({
          message: '',
          onClose: () => {
          },
        });
      },
      onSuccess: () => {
        setIsHidden(true);
      },
    });
  };

  const onSetPrimary = (contact: SellerContact) => {
    setIsLoading(true);

    const data: SellerUpdatePrimaryRequest = {
      ...sellerState,
      primary_contact_id: contact.id,
      primaryContact: contact,
    };

    // TODO: post launch - we may want to consider sending this to a separate,
    // "set primary contact" endpoint which only accepts a primary_contact_id,
    // rather than the entire seller object. This will make this request less
    // prone to errors & future breaking changes
    axios.put(routes.apiSellerUpdate(sellerState.id), data)
      .then((res: AxiosResponse<string>) => {
        if (res.status === 200) {
          setSellerState(data);
          setIsLoading(false);
          setSavedMessage(`${contact.name} set as primary contact.`);
        }
      })
      .catch((e: ApiV1Error) => {
        setIsLoading(false);

        if (isValidationError<Seller>(e)) {
          if (e.response.data.errors.primary_contact_id) {
            setErrorMessage(`Could not set ${contact.name} as the primary contact. ${e.response.data.errors.primary_contact_id.join(' ')}`);
          } else {
            setErrorMessage(
              <>
                Could not set
                {' '}
                {contact.name}
                {' '}
                as the primary contact because there are errors with your seller profile. Please
                {' '}
                <a href={routes.adminSellerEdit(seller.slug)}>
                  update your seller profile
                </a>
                {' '}
                before continuing.
              </>,
            );
          }
        } else if (isCSRFError(e)) {
          setErrorMessage('This page must be refreshed.');
        } else {
          setErrorMessage(`An error occurred while setting ${contact.name} as the primary contact`);
        }
      });
  };

  // Save handler. Updates a seller contact
  const onSave = (
    contact: SellerContact,
    {
      setErrors,
      setSubmitting,
    }: FormikHelpers<SellerContact>,
    setFormError: (message: string) => void,
    setFormSubmitting: (submitting: boolean) => void,
  ): void => {
    // Tell formik we're submitting
    setSubmitting(true);
    // Tell this component we're submitting
    setIsLoading(true);
    // Tell the EditSellerContact form we're submitting
    setFormSubmitting(true);

    // Format phone number for submission
    // Creates a new object to avoid mutating the `contact` object
    const data: SellerContact = {
      ...contact,
      phone: contact.phone ? contact.phone.replace(/\D/g, '') : '',
      alternate_phone: contact.alternate_phone ? contact.alternate_phone.replace(/\D/g, '') : '',
    };

    let promise;
    if (data.id) {
      promise = axios.put(routes.apiSellerContactsUpdate(contact.id), data);
    } else {
      promise = axios.post(routes.API_SELLER_CONTACTS_POST, data);
    }

    promise.then((res: AxiosResponse<{ data: SellerContact }>) => {
      if (res.status >= 200 || res.status < 300) {
        setCurrentlyEditing(undefined);
        setIsLoading(false);
        setSavedMessage(`${res.data.data.name} saved.`);

        if (data.id) {
          // Sets the contacts in the table to all the other contacts, plus the newly
          // edited contact
          setContactsState(contactsState.map((mapContact) => {
            return mapContact.id === res.data.data.id ? res.data.data : mapContact;
          }));
        } else {
          // Sets the contacts in the table to all add the new contact
          setContactsState([
            ...contactsState,
            res.data.data,
          ]);
        }
      }
    })
      .catch((error: AxiosError) => {
        setIsLoading(false);
        const statusCode: number | undefined = error.response?.status;

        if (statusCode && statusCode === 422) {
          const { errors }: { errors: LaravelValidationErrors } = error.response?.data;
          setFormError('Please check your inputs and try again.');

          // Sets formik errors
          setErrors(errors);
        } else if (statusCode && statusCode === 419) {
          setFormError('This form must be refreshed.');
        } else {
          setFormError('An unknown error occurred. Please try again later.');
        }

        // If error, then we need to end the submission. We don't need to do this on success
        // because the form is closed. If we put this in the finally() block, we get a react
        // memory leak.
        setSubmitting(false);
        setFormSubmitting(false);
      });
  };

  return (
    <>
      <div className="mb-3 d-flex justify-content-between flex-wrap flex-md-nowrap">
        <h1 className="mb-0">{`Manage Contacts For Profile: ${sellerState.businessName}`}</h1>
      </div>

      {
        !!errorMessage
        && (
          <Alert
            variant="danger"
            dismissible={true}
            onClose={() => setErrorMessage('')}
          >
            {errorMessage}
          </Alert>
        )
      }

      {
        !!savedMessage
        && (
          <Alert
            variant="success"
            dismissible={true}
            onClose={() => setSavedMessage('')}
          >
            {savedMessage}
          </Alert>
        )
      }

      {/* TODO: Post launch - Should revamp styles, especially for mobile. For one, it shouldn't
      use a table layout. Also, the way the EditSellerContact component opens at the bottom of
      the page may be confusing, especially on mobile, where it isn't instantly visible */}
      <div className="contacts py-2">
        <ContactsTable>
          {
            contactsState.map((contact, index) => {
              return (
                <ContactLine
                  contact={contact}
                  key={contact.id}
                  currentlyEditing={currentlyEditing}
                  onEdit={setCurrentlyEditing}
                  onDelete={onDelete}
                  onSetPrimary={onSetPrimary}
                  isLoading={isLoading || !!currentlyEditing}
                  isPrimary={
                    contact.id === primaryContact?.id
                    // If no primary contact in state, indicate that the first contact is primary
                    || (!primaryContact && index === 0)
                  }
                />
              );
            })
          }
        </ContactsTable>
        {
          !!currentlyEditing
          && (
            <EditSellerContact
              contact={currentlyEditing}
              onSave={onSave}
              onCancel={onCancel}
            />
          )
        }
      </div>
      {!isHidden && (
        <ConfirmationModal
          show={showModal}
          setShow={setShowModal}
          {...modalData}
        />
      )}
    </>
  );
};

export default ManageContacts;

if (document.querySelectorAll('.react-manage-contacts').length > 0) {
  document.querySelectorAll('.react-manage-contacts')
    .forEach((element) => {
      const contacts: SellerContact[] = JSON.parse(element.getAttribute('data-contacts') ?? '{}').data;
      const seller: Seller = JSON.parse(element.getAttribute('data-seller') ?? '{}');

      ReactDOM.render(<ManageContacts contacts={contacts} seller={seller} />, element);
    });
}
